import { type FC, type FormEvent, useState } from "react";
import { useNavigate } from "react-router";

import { useMutation, useQuery } from "@apollo/client/react";

import { faCalendarStar } from "@fortawesome/pro-duotone-svg-icons/faCalendarStar";
import { faIdCardClip } from "@fortawesome/pro-duotone-svg-icons/faIdCardClip";

import {
  DashboardProjectsAssigneesDocument,
  MeDocument,
  ProjectBuildDocument,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { InputField } from "@styled/input_field";
import { Notification } from "@styled/notification";
import { SelectField } from "@styled/select_field";
import { Sentence } from "@styled/sentence";

import { newProjectAssignment } from "./new_project_assignment";

// The New Case form, shared by the modal on a populated cases list and the
// inline panel in the guided empty state. `Assign To` is POLICY-GATED, not
// disabled — omitted entirely when `me.projectPolicy.assign` is false, which is
// the behaviour the modal has always had. Staff must pick an owner when the org
// has a clinician (see `newProjectAssignment`).

export const useNewProjectForm = ({
  onCreated,
}: {
  onCreated?: () => void;
} = {}) => {
  const navigate = useNavigate();
  const [execute, { loading, data }] = useMutation(ProjectBuildDocument);
  const errors = data?.projects.build.errors;

  const { data: meData } = useQuery(MeDocument);
  const canAssign = meData?.me?.projectPolicy?.assign ?? false;

  // `users` and `memberships` authorize admin or staff only, so asking for them
  // as a clinician raises PERMISSIONS. The guided empty state mounts this
  // hook on page load rather than on opening a modal, so an unskipped query
  // would throw on first paint for exactly the users it was built for.
  const { data: assigneesData } = useQuery(DashboardProjectsAssigneesDocument, {
    skip: !canAssign,
  });

  const [name, setName] = useState("");
  const [claimantDob, setClaimantDob] = useState("");
  const [pickedUserId, setUserId] = useState("");

  const assignment = newProjectAssignment({
    role: meData?.me?.role,
    memberships: assigneesData?.memberships,
  });
  const userId = pickedUserId || assignment.defaultUserId;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!name) return;
    if (assignment.required && !userId) return;

    const result = await execute({
      variables: {
        input: {
          name,
          claimantDob: claimantDob || undefined,
          userId: canAssign && userId ? userId : undefined,
        },
      },
      refetchQueries: ["DashboardProjectsList"],
    });

    const project = result.data?.projects.build.project;
    if (project) {
      track("project_created", { project_id: project.id });
      onCreated?.();
      navigate(`/dashboard/projects/${project.id}`);
    }
  };

  return {
    name,
    setName,
    claimantDob,
    setClaimantDob,
    userId,
    setUserId,
    canAssign,
    assignRequired: assignment.required,
    users: assigneesData?.users ?? [],
    errors,
    loading,
    onSubmit,
  } as const;
};

export type NewProjectForm = ReturnType<typeof useNewProjectForm>;

export const NewProjectFields: FC<{ form: NewProjectForm }> = ({ form }) => (
  <>
    {form.errors?.base && (
      <Notification color="rose">
        <Sentence>{form.errors.base}</Sentence>
      </Notification>
    )}

    <InputField
      required
      id="name"
      name="name"
      type="text"
      label="Name:"
      placeholder="Claimant Name"
      icon={faIdCardClip}
      value={form.name}
      errors={form.errors}
      onChange={(event) => form.setName(event.target.value)}
    />

    <InputField
      id="claimantDob"
      name="claimantDob"
      type="date"
      label="Claimant Date of Birth:"
      icon={faCalendarStar}
      value={form.claimantDob}
      errors={form.errors}
      onChange={(event) => form.setClaimantDob(event.target.value)}
    />

    {form.canAssign && (
      <SelectField
        id="userId"
        name="userId"
        label="Assign To:"
        required={form.assignRequired}
        value={form.userId}
        onChange={(event) => form.setUserId(event.target.value)}
      >
        <option value="" disabled={form.assignRequired}>
          Select a user...
        </option>
        {form.users.map((user) => (
          <option key={user.id} value={user.id}>
            {user.name} ({user.email})
          </option>
        ))}
      </SelectField>
    )}
  </>
);
